import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useRoles } from "../../../context/roles-context.tsx";
import useAccountEnhanced from "src/hooks/use-account-enhanced";

import {
    TrackingPartner,
    GetTrackingPartners,
    SetTrackingPartner,
    GetCustomerHistory,
} from "src/api/partner-management.api";

import { Card } from "src/components/card.tsx";
import { Toast } from "../components/toast.tsx";
import { EditForm } from "../components/edit-form.tsx";
import { LeftSidebar } from "src/components/left-sidebar.tsx";
import { Instruction } from "src/components/instruction.tsx";
import { SearchControls } from "../components/search-controls.tsx";

import { createColumnHelper } from "@tanstack/react-table";
import { HiPencil, HiViewList } from "react-icons/hi";
import { PiAddressBookTabsDuotone } from "react-icons/pi";
import { SimpleTable } from "src/components/simple-table.tsx";

import { GetVendors } from "src/api/partner-management.api";
import { Vendor } from "../types/vendor";

export function IndexPage() {
    // USER & ROLES
    const roles = useRoles();
    const readAccessTPM = roles.checkHasRole("TrackingPartnerMgmt.Reader");
    const writeAccessTPM = roles.checkHasRole("TrackingPartnerMgmt.Writer");
    const accountEnhanced = useAccountEnhanced();
    const currentUser = accountEnhanced.me.employeeCode;

    // TOAST NOTIFICATIONS
    const [toastVariant, setToastVariant] = useState<string>("");
    const [toastMessage, setToastMessage] = useState<string>("");
    const [showToast, setShowToast] = useState<boolean>(false);

    const displayToast = (variant: string, message: string) => {
        setToastVariant(variant);
        setToastMessage(message);

        setShowToast(true);
        setTimeout(() => {
            // After 5 seconds hide the toast
            setShowToast(false);
        }, 5000);
    };

    const defaultData: TrackingPartner = {
        Id: null,
        Partner: "",
        CustomerCode: "",
        Requestor: null,
        Status: "ACTIVE",
        Active: true,
        BOLRegex: null,
        BOLLocation: "ORCSH#",
        BOLType: "ORCSH#",
        ReferenceKey: null,
        Author: null,
        ModifiedAt: null,
    };

    const resetFormData = (partner: string, customer: string) => {
        formData.Id = defaultData.Id;
        formData.Partner = partner;
        formData.CustomerCode = customer;
        formData.Requestor = defaultData.Requestor;
        formData.Status = defaultData.Status;
        formData.Active = defaultData.Active;
        formData.BOLRegex = defaultData.BOLRegex;
        formData.BOLLocation = defaultData.BOLLocation;
        formData.BOLType = defaultData.BOLType;
        formData.ReferenceKey = defaultData.ReferenceKey;
    };

    // COLLECT VENDOR - On page load
    const [vendors, setVendors] = useState<Vendor[]>([]);
    useEffect(() => {
        const collectVendors = async () => setVendors(await GetVendors());
        collectVendors();
    }, []);

    // SEARCH PARTNERS
    const [partners, setPartners] = useState<TrackingPartner[]>([]);
    const [loading, setLoading] = useState<boolean | null>(null);
    const [loadError, setLoadError] = useState<boolean>(false);
    const [selectedParnter, setSelectedParnter] = useState<string>("ALL");

    const handleSearch = async (
        partnerSearch: string,
        customerSearch: string,
    ) => {
        var success = false;
        try {
            setLoading(true);
            setSelectedParnter(partnerSearch);

            const data = await GetTrackingPartners(
                partnerSearch,
                customerSearch ?? "",
            );
            setPartners(data);
            success = true;
        } catch (error) {
            console.log(error);
            displayToast("Error", "Failed to collect tracking partners!");
        } finally {
            setLoading(false);
            setLoadError(!success);

            // Populate new registration fields
            resetFormData(partnerSearch, customerSearch?.toUpperCase());
        }
    };

    // ADD & EDIT PARTNER
    const [formData, setFormData] = useState<TrackingPartner>(defaultData);
    const [saving, setSaving] = useState<boolean>(false);
    const [editing, setEditing] = useState<boolean>(false);

    const showFormModal = (visible: boolean) => {
        setEditing(visible);
        var modal = document.getElementById("formModal") as HTMLDialogElement;
        if (visible) {
            modal.showModal();
        } else {
            modal.close();
        }
    };

    const handleEdit = (row: TrackingPartner | null = null) => {
        console.log("EDITING!");
        if (row != null) setFormData({ ...row });
        showFormModal(true);
    };

    const handleCancel = () => {
        console.log("CANCELLING!");

        showFormModal(false);
        return;
    };

    const handleSave = async (data: TrackingPartner) => {
        console.log("SAVING!");
        setSaving(true);

        var success = false;
        try {
            data.BOLLocation =
                data.BOLType == "" ? data.ReferenceKey : data.BOLType;

            var response = await SetTrackingPartner(currentUser, data.Id, data);
            success = response.status == 200;
            if (success) {
                // Refresh page
                handleSearch(
                    data.Partner,
                    data.Id == null ? data.CustomerCode : "",
                );
            }
        } catch (error) {
            console.log(error);
        } finally {
            setSaving(false);
            showFormModal(false);

            displayToast(
                success ? "Success" : "Error",
                success
                    ? data.Id == null
                        ? "Added new tracking partner!"
                        : "Updated tracking partner!"
                    : data.Id == null
                    ? "Failed to add new tracking partner!"
                    : "Failed to udpate tracking partner!",
            );
        }
    };

    // Set Table Columns
    const columnHelper = createColumnHelper<TrackingPartner>();
    const partnerColumns = [
        columnHelper.accessor("Partner", {
            header: "Partner",
            cell: (info) => {
                return vendors.filter(
                    (v) => v.TrackingPartner == info.getValue(),
                )[0]?.DisplayName;
            },
            size: 200,
        }),
        columnHelper.accessor("CustomerCode", {
            header: "Customer",
            cell: (info) => info.getValue(),
            size: 200,
        }),
        columnHelper.accessor("Requestor", {
            header: "Requestor",
            cell: (info) => info.getValue(),
            size: 200,
        }),
        columnHelper.accessor("BOLLocation", {
            header: "BOL Location",
            cell: (info) => info.getValue(),
            size: 200,
        }),
        columnHelper.accessor("Author", {
            header: "Author",
            cell: (info) => info.getValue(),
            size: 200,
        }),
        columnHelper.accessor("ModifiedAt", {
            header: "Updated",
            cell: (info) => {
                return dayjs(info.getValue()).format("MMM DD, YYYY");
            },
            size: 200,
        }),
        columnHelper.accessor("Active", {
            header: "Status",
            cell: (info) => {
                return (
                    <div
                        className={
                            info.getValue()
                                ? "w-20 badge badge-primary"
                                : "w-20 badge badge-primary badge-outline"
                        }
                    >
                        {info.getValue() ? "Active" : "Inactive"}
                    </div>
                );
            },
            size: 130,
        }),
        columnHelper.accessor("Id", {
            header: "",
            cell: (info) => {
                return (
                    <div className="flex justify-end gap-2">
                        <button
                            className="btn btn-sm btn-link p-0"
                            onClick={() => {
                                getHistory(partners[info.row.index].Id);
                            }}
                        >
                            <HiViewList className="w-6 h-6" />
                        </button>

                        {writeAccessTPM ? (
                            <button
                                className="btn btn-sm btn-link p-0"
                                onClick={() =>
                                    handleEdit(partners[info.row.index])
                                }
                            >
                                <HiPencil className="w-6 h-6" />
                            </button>
                        ) : (
                            ""
                        )}
                    </div>
                );
            },
            size: 130,
        }),
    ];

    const historyModal = document.getElementById(
        "historyModal",
    ) as HTMLDialogElement;
    const getHistory = async (Id: number | null) => {
        const history = await GetCustomerHistory(Id);
        console.log(history);
        setPartnerHistory(history);
        historyModal.showModal();
    };

    const [partnerHistory, setPartnerHistory] = useState<TrackingPartner[]>([]);

    const historyColumnHelper = createColumnHelper<TrackingPartner>();
    const historyPartnerColumns = [
        historyColumnHelper.accessor("Partner", {
            header: "Partner",
            cell: (info) => info.getValue(),
            size: 150,
        }),
        historyColumnHelper.accessor("CustomerCode", {
            header: "Customer",
            cell: (info) => info.getValue()?.toUpperCase(),
            size: 150,
        }),
        historyColumnHelper.accessor("Requestor", {
            header: "Requestor",
            cell: (info) => info.getValue(),
            size: 150,
        }),
        historyColumnHelper.accessor("BOLLocation", {
            header: "BOL Location",
            cell: (info) => info.getValue(),
            size: 120,
        }),
        historyColumnHelper.accessor("Author", {
            header: "Author",
            cell: (info) => info.getValue()?.toUpperCase(),
            size: 100,
        }),
        historyColumnHelper.accessor("Active", {
            header: "Status",
            cell: (info) => {
                return (
                    <div
                        className={
                            info.getValue()
                                ? "w-20 badge badge-primary"
                                : "w-20 badge badge-primary badge-outline"
                        }
                    >
                        {info.getValue() ? "Active" : "Inactive"}
                    </div>
                );
            },
            size: 120,
        }),
        historyColumnHelper.accessor("ModifiedAt", {
            header: "Updated",
            cell: (info) => {
                return dayjs(info.getValue()).format("MMM DD, YYYY");
            },
            size: 120,
        }),
    ];

    return (
        <>
            {writeAccessTPM || readAccessTPM ? (
                <>
                    <Toast
                        variant={toastVariant}
                        message={toastMessage}
                        visible={showToast}
                    />

                    <dialog
                        id="formModal"
                        className="modal"
                        data-theme="corporate"
                    >
                        {editing ? (
                            <EditForm
                                formData={formData}
                                handleSubmit={handleSave}
                                handleCancel={handleCancel}
                                saving={saving}
                            />
                        ) : (
                            ""
                        )}
                    </dialog>

                    <div
                        className="flex h-full bg-base-200"
                        data-theme="corporate"
                    >
                        <LeftSidebar
                            icon={
                                <PiAddressBookTabsDuotone className="w-8 h-8 mr-2 text-[#ac2226]" />
                            }
                            title="Tracking Partners"
                        >
                            <SearchControls
                                options={vendors}
                                handleSubmit={handleSearch}
                                loading={loading}
                            />
                        </LeftSidebar>

                        {/* Main content */}
                        <div className="flex-1 flex flex-col overflow-hidden p-8">
                            <div className="h-full flex flex-col">
                                {loading == null ? (
                                    <Instruction
                                        icon={
                                            <PiAddressBookTabsDuotone className="w-16 h-16 text-[#ac2226]" />
                                        }
                                        title="No Partners Specified Yet"
                                        description="Use the sidebar to search by partner or customer code."
                                    />
                                ) : (
                                    <div className="h-full max-h-200">
                                        <SimpleTable
                                            data={partners}
                                            columns={partnerColumns}
                                            tableSize="md"
                                            loading={loading}
                                            loadError={loadError}
                                            icon={
                                                <PiAddressBookTabsDuotone className="w-8 h-8 text-[#ac2226]" />
                                            }
                                            errorTitle="Whoops!"
                                            errorText="Something went wrong behind the scenes, sorry. Please try again later."
                                            emptyTitle="Hmm, nothing here..."
                                            emptyText={
                                                selectedParnter == "ALL"
                                                    ? "Looks like we don't have that customer registration. Please update your search with a partner selection to begin registration."
                                                    : "Looks like we don't have that customer registration. Would you like to add it?"
                                            }
                                            emptyAction={
                                                selectedParnter == "ALL" ? (
                                                    <></>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-outline btn-primary"
                                                        onClick={() =>
                                                            handleEdit()
                                                        }
                                                    >
                                                        <HiPencil className="w-6 h-6" />
                                                        Add Customer
                                                    </button>
                                                )
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <dialog
                        id="historyModal"
                        className="modal"
                        data-theme="corporate"
                    >
                        <Card
                            customization={
                                "max-w-5xl bg-gray-50 border-gray-200 justify-self-center"
                            }
                        >
                            <div className="flex flex-col items-center justify-center m-6">
                                <h2 className="text-xl font-bold text-gray-700 mb-2">
                                    Partner History
                                </h2>
                                <p className="text-gray-600 mb-4">
                                    The change history of this tracking partner
                                    record.
                                </p>

                                <SimpleTable
                                    data={partnerHistory}
                                    columns={historyPartnerColumns}
                                    tableSize="xs"
                                    loading={false}
                                    loadError={false}
                                    icon={
                                        <PiAddressBookTabsDuotone className="w-8 h-8 text-[#ac2226]" />
                                    }
                                    errorTitle=""
                                    errorText=""
                                    emptyTitle=""
                                    emptyText={""}
                                    emptyAction={null}
                                />

                                <button
                                    type="reset"
                                    className="btn btn-link mt-2"
                                    onClick={() => {
                                        historyModal.close();
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </Card>
                    </dialog>
                </>
            ) : (
                <div className="p-4 label-text text-white">
                    Permission denied, you shouldn't be here...
                </div>
            )}
        </>
    );
}
